import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import CheckIcon from '@mui/icons-material/Check'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import InCardStack from 'components/atoms/container/InCardStack'
import LinearProgressBar from 'components/atoms/LinearProgressBar'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import RowStack from 'components/atoms/container/RowStack'
import LikertScale from 'components/molecules/LikertScale'
import AssessmentLanding from 'components/molecules/assessments/AssessmentLanding'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getConstant } from 'services/constantServices'
import {
  ASSESSMENT_URL,
  ASSESSMENT_RECIPIENTS_URL,
  ASSESSMENT_RECIPIENT_STATUSES,
  CONSTANTS_OPTIONS
} from 'services/constants'
import { getPercentageDisplay } from 'utils/formatText'
import { getGoalInfoById } from 'utils/getSupportingInfo'
import {
  BackIconButton,
  NextIconButton,
  RoundIconButton
} from 'components/atoms/button/StyledButton'
import { postRequest, patchRequest } from 'utils/api'
import OverlayLoading from 'components/atoms/OverlayLoading'
import NotificationSnackBar from 'components/molecules/notificationOverlay/NotificationSnackBar'

const AssessmentCard = ({
  goalId,
  selfAssessment,
  assessmentSenderName, // For others assessment
  assessmentRecipientId, // For others assessment
  questions,
  submittedCallback
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [started, setStarted] = useState(false)
  const [goalData, setGoalData] = useState({})
  const [options, setOptions] = useState([])
  const [answers, setAnswers] = useState([])
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [naOption, setNAOption] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const questionField = selfAssessment ? 'selfQuestion' : 'othersQuestion'

  useEffect(() => {
    const fetchData = async () => {
      const evaluateOptions = await getConstant(
        CONSTANTS_OPTIONS.EVALUATION_OPTIONS
      )
      if (evaluateOptions) {
        setOptions(evaluateOptions.filter(o => o.value !== 0))
        setNAOption(evaluateOptions.find(o => o.value === 0))
      }
      const goal = await getGoalInfoById(goalId)
      setGoalData(goal)
    }
    if (goalId) {
      fetchData()
    }
  }, [goalId])

  useEffect(() => {
    if (questions.length) {
      setAnswers(Array(questions.length).fill(null))
    }
  }, [questions])

  const handleSubmit = async () => {
    setLoading(true)
    const selfScores = {}
    questions.forEach((q, index) => {
      selfScores[q.id] = parseInt(answers[index], 10)
    })

    let res
    if (selfAssessment) {
      res = await postRequest(ASSESSMENT_URL, {
        goalId,
        version: questions[0].version,
        selfScores
      })
    } else {
      res = await patchRequest(
        `${ASSESSMENT_RECIPIENTS_URL}/${assessmentRecipientId}`,
        {
          score: selfScores,
          status: ASSESSMENT_RECIPIENT_STATUSES.COMPLETED
        }
      )
    }
    if (res.status === 201 || res.status === 200) {
      submittedCallback(res.data.data)
    } else {
      setError(true)
    }
    setLoading(false)
  }

  let myTimeout = null
  const updateAnswer = (idx, choice, moveToNext = true, timeout = 500) => {
    myTimeout = setTimeout(() => {
      const newAnswers = [...answers]
      newAnswers[idx] = choice
      setAnswers(newAnswers)
      if (moveToNext) {
        handleNext()
      }
    }, timeout)
    return () => clearTimeout(myTimeout)
  }

  const answeredPercent = () =>
    answers.filter(x => x !== null).length / questions.length

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1)
    }
  }

  const currentNASelected = () => answers[currentQuestion] === naOption?.value
  const handleNAClick = () => {
    const updatedValue = currentNASelected() ? null : naOption.value
    updateAnswer(currentQuestion, updatedValue, false, 0)
    if (
      currentQuestion < questions.length - 1 &&
      !currentNASelected() &&
      answers[currentQuestion + 1] === null
    )
      setCurrentQuestion(currentQuestion + 1)
  }
  return (
    <>
      {!started ? (
        <AssessmentLanding
          selfAssessment={selfAssessment}
          goalName={goalData.leadershipPathName}
          handleBegin={() => setStarted(true)}
          questionCount={questions.length}
          assessmentSenderName={assessmentSenderName}
        />
      ) : (
        <InCardStack width='100%'>
          <Typography variant='h1' color='primary'>
            {selfAssessment && t('assessments.selfAssessment')}
          </Typography>
          <BoxWithBackground
            padding={20}
            sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Grid container maxWidth={1140} width={'100%'}>
              <Grid size={5} paddingY={32}>
                <Stack spacing={4} padding={0}>
                  {selfAssessment ? (
                    <>
                      <Typography variant='h2'>{goalData?.name}</Typography>
                      <Typography variant='body1Large'>
                        {goalData?.leadershipModelName}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Stack spacing={0} padding={0}>
                        <Typography variant='h1'>{`${assessmentSenderName}'s`}</Typography>
                        <Typography variant='h3'>
                          {t('assessmentForOthers._360Assessment')}
                        </Typography>
                      </Stack>
                      <Typography variant='body1'>
                        <strong>{goalData?.name}: </strong>
                        {goalData?.leadershipModelName}
                      </Typography>
                    </>
                  )}
                </Stack>
              </Grid>
              <Grid size={7}>
                <InCardStack spacing={4} data-testid='question-body'>
                  <RowStack justifyContent='space-between'>
                    <Typography>
                      {t('assessments.assessmentProgress')}
                    </Typography>
                    <Typography>
                      {getPercentageDisplay(answeredPercent(), 0)}
                    </Typography>
                  </RowStack>
                  <RowStack width='100%' display='flex'>
                    <Typography>
                      {t('assessments.mOfN', {
                        m: currentQuestion + 1,
                        n: questions.length
                      })}
                    </Typography>
                    <Box flexGrow={1}>
                      <LinearProgressBar
                        value={answeredPercent() * 100}
                        width='100%'
                      />
                    </Box>
                  </RowStack>
                  <BoxWithBackground
                    backgroundColor={theme.palette.background.paper}
                    width='100%'>
                    {questions.map(
                      (q, index) =>
                        index === currentQuestion && (
                          <Stack
                            padding={16}
                            spacing={6}
                            key={`assessment-question-${q.id}`}>
                            <Typography variant='body1Large' minHeight={54}>
                              {`${index + 1}. `}
                              {q[questionField]}
                            </Typography>
                            <LikertScale
                              id={index + 1}
                              onChange={v => updateAnswer(index, v)}
                              initialValue={answers[index]}
                              options={options}
                              setValueFromOthers={true}
                              paddingSX={{ paddingX: 0 }}
                            />
                          </Stack>
                        )
                    )}
                  </BoxWithBackground>
                  <RowStack
                    justifyContent='space-between'
                    data-testid='navigation-bar'>
                    <Box width={48}>
                      {currentQuestion > 0 && (
                        <BackIconButton
                          inverseTheme={true}
                          onClick={() =>
                            setCurrentQuestion(currentQuestion - 1)
                          }
                        />
                      )}
                    </Box>
                    <Button
                      variant={
                        answers[currentQuestion] === naOption?.value
                          ? 'contained'
                          : 'outlined'
                      }
                      size='small'
                      color={
                        answers[currentQuestion] === naOption?.value
                          ? 'secondary'
                          : 'primary'
                      }
                      startIcon={
                        answers[currentQuestion] === naOption?.value ? (
                          <RadioButtonCheckedIcon />
                        ) : (
                          <RadioButtonUncheckedIcon />
                        )
                      }
                      onClick={handleNAClick}>
                      {t('assessments.naSkip')}
                    </Button>
                    {currentQuestion < questions.length - 1 ? (
                      <NextIconButton
                        disabled={answers[currentQuestion] === null}
                        onClick={handleNext}
                      />
                    ) : (
                      <RoundIconButton
                        icon={<CheckIcon />}
                        disabled={answeredPercent() < 1}
                        onClick={() => handleSubmit()}
                        data-testid='submit-button'
                      />
                    )}
                  </RowStack>
                </InCardStack>
              </Grid>
            </Grid>
          </BoxWithBackground>
        </InCardStack>
      )}

      {loading && <OverlayLoading open={loading} />}
      <NotificationSnackBar
        error={error}
        open={error}
        setOpen={setError}
        autoHideDuration={5000}
      />
    </>
  )
}

export default AssessmentCard
